import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import ERFPaymentAdjustmentDetails from "./ERFPaymentAdjustmentDetails";
// eslint-disable-next-line
import Loader from "../../../../Components/Loader";
import { useSelector } from "react-redux";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";

const ERF_TYPES_WITH_SECURITY = ["Rent", "Sale & Rent"];

const ERFPaymentSection = ({
  documentNumber,
  allowAddBilling = true,
  allowAddSecurity = true,
  allowAddCreditNote = true,
  allowUpdatePayentButton = true,
}) => {
  let userData = useSelector((state) => state?.user);

  const [financeExectiveUserRoleData, setFinanceExecutiveUserRoleData] =
    useState();
  // eslint-disable-next-line
  const [isDispatchManager, setIsDispatchManager] = useState(false);
  const [isServiceExecutive, setIsServiceExecutive] = useState(false);
  const [currentUserName, setCurrentUserName] = useState("");

  const [isAdminOrPCD, setIsAdminOrPCD] = useState(false);
  const [isSalesAdmin, setIsSalesAdmin] = useState(false);

  const [collectionStatus, setCollectionStatus] = useState(null);

  useEffect(() => {
    if (userData?.data?.roles) {
      const allowedRoles = ["FI-EXECUTIVE"];
      const isAllowed = userData?.data?.roles.some((r) =>
        allowedRoles.includes(r.roleId),
      );
      setFinanceExecutiveUserRoleData(isAllowed);

      // dispatch manager check
      const isDispatch = userData?.data?.roles.some(
        (r) => r.roleId === "DISPATCH",
      );
      setIsDispatchManager(isDispatch);

      const isServiceExecutive = userData?.data?.roles.some(
        (r) => r.roleId === "SERVICE-EXECUTIVE",
      );
      setIsServiceExecutive(isServiceExecutive);

      const isPCDOrAdmin = userData?.data?.roles.some((r) =>
        ["SALES-ADMIN", "SALES-PCD"].includes(r.roleId),
      );
      setIsAdminOrPCD(isPCDOrAdmin);

      const isSalesAdmin = userData?.data?.roles.some(
        (r) => r.roleId === "SALES-ADMIN",
      );
      setIsSalesAdmin(isSalesAdmin);

      setCurrentUserName(
        userData?.data?.firstName ||
          userData?.data?.name ||
          userData?.data?.userId ||
          "",
      );
    }
  }, [userData]);
  const [loading, setLoading] = useState(false);
  const [erfRecord, setErfRecord] = useState(null);
  const [showSecurity, setShowSecurity] = useState(false);

  // Existing rows passed to child as read-only props (no local_id)
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [securityHistory, setSecurityHistory] = useState([]);
  const [creditNoteHistory, setCreditNoteHistory] = useState([]);
  const [serviceExecutiveList, setServiceExecutiveList] = useState([]);

  // Full merged arrays (existing + new rows) — updated live from child
  const [latestData, setLatestData] = useState({
    paymentHistory: [],
    securityHistory: [],
    creditNoteHistory: [],
  });

  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);
  const [failureModalData] = useState({
    title: "Validation Error",
    ErrorMsg: "Please check the required fields.",
  });

  // ── FETCH ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (documentNumber) fetchERFData();
    // eslint-disable-next-line
  }, [documentNumber]);

  const fetchERFData = async () => {
    setLoading(true);
    try {
      const response = await ItemService.generateQuery({
        queryName: "ERFDetail",
        documentNumber: documentNumber,
        timeZone: "Asia/Calcutta",
      });

      if (
        response?.status === 200 &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        // Take the LAST record — it is the most recent version of this ERF
        const lastRecord = response.data[response.data.length - 1];
        setErfRecord(lastRecord);

        const hasSecuritySection = ERF_TYPES_WITH_SECURITY.includes(
          lastRecord.erfType ?? "",
        );
        setShowSecurity(hasSecuritySection);

        buildDisplayState(lastRecord, hasSecuritySection);
      } else {
        toast.error("No ERF data found for this document");
      }
    } catch (err) {
      console.error("ERFExtDetail fetch error:", err);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRow = async (tableKey, rowIndex, role, { remarks }) => {
    const tableMap = {
      billing: { rows: paymentHistory, setter: setPaymentHistory },
      security: { rows: securityHistory, setter: setSecurityHistory },
      creditNote: { rows: creditNoteHistory, setter: setCreditNoteHistory },
    };

    const { rows, setter } = tableMap[tableKey];
    const row = rows[rowIndex];
    if (!row) return;

    const confirmationKey =
      role === "serviceExecutive" ? "seConfirmation" : "financeConfirmation";

    const updatedRow = {
      ...row,
      [confirmationKey]: {
        confirmed: true,
        remarks,
        confirmedBy: currentUserName,
        confirmedAt: new Date().toISOString(),
      },
    };

    const updatedRows = rows.map((r, i) => (i === rowIndex ? updatedRow : r));

    setLoading(true);
    try {
      const isRefundClearTrigger =
        role === "financeExecutive" &&
        tableKey === "creditNote" &&
        row.status === "Refund";

      const isCreditNoteClearTrigger =
        role === "financeExecutive" &&
        tableKey === "creditNote" &&
        row.status === "Credit Note";

      const payload = {
        documentNumber: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.documentNumber,
        paymentHistory: tableKey === "billing" ? updatedRows : paymentHistory,
        securityHistory:
          tableKey === "security" ? updatedRows : securityHistory,
        creditNoteHistory:
          tableKey === "creditNote" ? updatedRows : creditNoteHistory,
        ...(isRefundClearTrigger && { refundAmountIndicator: false }),
        ...(isCreditNoteClearTrigger && { creditNoteIndicator: false }),
      };

      await ItemService.updateEquipmentRentForm(payload);
      toast.success(
        role === "serviceExecutive" ? "Payment confirmed" : "Payment approved",
      );

      // Update local state immediately so UI reflects change without full reload
      setter(updatedRows);
    } catch {
      toast.error("Failed to save confirmation");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    const fetchExecutives = async () => {
      try {
        const response = await ItemService.generateQuery({
          queryName: "get_ServiceProvider_list",
          page: 1,
          per_page: 999,
          timeZone: "Asia/Calcutta",
        });
        if (response?.status === 200 && Array.isArray(response.data)) {
          setServiceExecutiveList(
            response.data.map((item) => ({
              id: item.userId,
              name: item.firstName || item.name,
              code: item.userId,
            })),
          );
        }
      } catch (err) {
        console.error("Failed to load service executives", err);
      }
    };
    fetchExecutives();
  }, []);

  // ── BUILD DISPLAY STATE ───────────────────────────────────────────────────
  const buildDisplayState = (record, hasSecuritySection) => {
    setCollectionStatus(record.financeCollectionConfirmation ?? null);
    // Strips local_id from saved rows so they load as read-only in the child.
    // Child treats rows WITHOUT local_id as historical (read-only).
    const stripLocalId = (rows) =>
      (rows ?? []).map(({ local_id, ...rest }) => rest);

    // ── Case A: paymentHistory already exists on this ERF ─────────────────
    if (record.paymentHistory?.length > 0) {
      const billing = stripLocalId(record.paymentHistory);
      const security = hasSecuritySection
        ? stripLocalId(record.securityHistory)
        : [];
      const creditNote = stripLocalId(record.creditNoteHistory);

      setPaymentHistory(billing);
      setSecurityHistory(security);
      setCreditNoteHistory(creditNote);
      setLatestData({
        paymentHistory: billing,
        securityHistory: security,
        creditNoteHistory: creditNote,
      });
      return;
    }

    // ── Case B: First time — build initial rows from flat ERF fields ───────
    // No local_id on these rows → they display as read-only in the table.

    const initialBillingRow = {
      paymentDate: record.orderDate ?? "",
      salePoint: record.salePoint ?? "",
      receivedLocation: record.salePoint ?? "",
      billingTotalAmt: record.netAmount ?? 0, // total amount billed
      paymentMode: record.paymentMode ?? "",
      billingReceivedAmt: record.receivedAmount ?? 0, // amount collected on rent day
      chequeNo: record.chequeNo || "NA",
      cardNo: record.cardTransNo || "NA",
      bank: record.bankName || "NA",
      billingBalanceAmt: record.balanceAmount ?? 0, // outstanding → drives C
      sellerName: "",
    };

    const initialSecurityRow = hasSecuritySection
      ? {
          paymentDate: record.orderDate ?? "",
          salePoint: record.salePoint ?? "",
          receivedLocation: record.salePoint ?? "",
          securityTotalAmt: record.rentSecurityTotal ?? 0, // total security due
          paymentMode: record.securityAmtMode ?? "",
          securityReceivedAmt: record.receivedSecurityAmt ?? 0, // security collected on rent day
          chequeNo: record.securityChequeNo || "NA",
          cardNo: record.securityCardTransNo || "NA",
          bank: record.securityBankName || "NA",
          securityBalanceAmt: record.balanceSecurityAmt ?? 0, // outstanding → drives C
          sellerName: "",
        }
      : null;

    const billing = [initialBillingRow];
    const security = hasSecuritySection ? [initialSecurityRow] : [];
    const creditNote = stripLocalId(record.creditNoteHistory);

    setPaymentHistory(billing);
    setSecurityHistory(security);
    setCreditNoteHistory(creditNote);
    setLatestData({
      paymentHistory: billing,
      securityHistory: security,
      creditNoteHistory: creditNote,
    });
  };

  // ── RECEIVE LIVE UPDATES FROM CHILD ───────────────────────────────────────
  const handleDataChange = (updatedData) => {
    setLatestData(updatedData);
  };

  // ── VALIDATE NEW ROWS BEFORE SAVE
  const validateNewRows = (billingRows, securityRows) => {
    const errors = [];

    // ── Billing ──────────────────────────────────────────────────────────
    billingRows
      .filter((r) => r.local_id)
      .forEach((row, idx) => {
        const label = `Billing row ${idx + 1}`;
        if (!row.paymentDate) errors.push(`${label}: Payment Date is required`);
        if (!row.salePoint?.toString().trim())
          errors.push(`${label}: Sale Point is required`);
        if (!row.paymentMode) errors.push(`${label}: Payment Mode is required`);
        if (
          row.billingReceivedAmt === "" ||
          row.billingReceivedAmt === null ||
          row.billingReceivedAmt === undefined
        )
          errors.push(`${label}: Received Amount is required`);
      });

    // ── Security ─────────────────────────────────────────────────────────
    securityRows
      .filter((r) => r.local_id)
      .forEach((row, idx) => {
        const label = `Security row ${idx + 1}`;
        if (!row.paymentDate) errors.push(`${label}: Payment Date is required`);
        if (!row.salePoint?.toString().trim())
          errors.push(`${label}: Sale Point is required`);
        if (!row.paymentMode) errors.push(`${label}: Payment Mode is required`);
        if (
          row.securityReceivedAmt === "" ||
          row.securityReceivedAmt === null ||
          row.securityReceivedAmt === undefined
        )
          errors.push(`${label}: Received Amount is required`);
      });

    return errors;
  };

  // ── UPDATE PAYMENT ────────────────────────────────────────────────────────
  const handleUpdatePayment = async () => {
    if (!erfRecord) {
      toast.error("No ERF record loaded");
      return;
    }
    if (!latestData.paymentHistory?.length) {
      toast.error("No billing records to save");
      return;
    }

    const validationErrors = validateNewRows(
      latestData.paymentHistory,
      latestData.securityHistory,
    );

    if (validationErrors.length > 0) {
      setErrorFields(validationErrors);
      setShowFailureModal(true);
      return;
    }

    setLoading(true);
    try {
      const { paymentHistory, securityHistory, creditNoteHistory } = latestData;

      // ── Recalculate summary values ──────────────────────────────────────
      // A = total received from patient
      const totalBillingReceived = paymentHistory.reduce(
        (sum, row) => sum + (parseFloat(row.billingReceivedAmt) || 0),
        0,
      );
      const totalSecurityReceived = securityHistory.reduce(
        (sum, row) => sum + (parseFloat(row.securityReceivedAmt) || 0),
        0,
      );
      const A = totalBillingReceived + totalSecurityReceived;

      // // B = total credit notes issued
      // const B = creditNoteHistory.reduce(
      //   (sum, row) => sum + (parseFloat(row.amount) || 0),
      //   0,
      // );

      // B = credit notes only (rows with status "Refund" are NOT counted here)
      const B = creditNoteHistory
        .filter((row) => row.status !== "Refund")
        .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

      // Refund = ONLY rows the user explicitly marked as "Refund"
      const B_refund = creditNoteHistory
        .filter((row) => row.status === "Refund")
        .reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

      // C = last billing balance + last security balance
      const lastBillingBalance =
        parseFloat(paymentHistory.at(-1)?.billingBalanceAmt ?? 0) || 0;
      const lastSecurityBalance = showSecurity
        ? parseFloat(securityHistory.at(-1)?.securityBalanceAmt ?? 0) || 0
        : 0;
      const C = lastBillingBalance + lastSecurityBalance;

      const balAmt = Math.max(0, parseFloat((C - B).toFixed(2)));
      // const returnedAmount = Math.max(0, parseFloat((B - C).toFixed(2)));
      const returnedAmount = parseFloat(B_refund.toFixed(2));

      const hasNewRows =
        paymentHistory.some((r) => r.local_id) ||
        securityHistory.some((r) => r.local_id);

      // ── Build payload ───────────────────────────────────────────────────
      const payloadData = {
        // Identity — backend needs these to locate the document
        documentNumber: erfRecord.documentNumber,
        equipmentNr: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.equipmentRentNr,

        // The 3 history arrays — local_id is kept for stable ordering on next load
        paymentHistory: paymentHistory,
        securityHistory: securityHistory,
        creditNoteHistory: creditNoteHistory,

        // Summary snapshot saved for reporting purposes
        paymentSummary: {
          A: parseFloat(A.toFixed(2)),
          B: parseFloat(B.toFixed(2)),
          C: parseFloat(C.toFixed(2)),
          totalBillingReceived: parseFloat(totalBillingReceived.toFixed(2)),
          totalSecurityReceived: parseFloat(totalSecurityReceived.toFixed(2)),
          totalCreditNote: parseFloat(B.toFixed(2)),
          balAmt: balAmt,
          returnedAmount: returnedAmount,
        },

        ...(hasNewRows && { financeCollectionConfirmation: "Not Confirmed" }),
      };

      // console.log("Payload for paymentSection", payloadData)

      await ItemService.updateEquipmentRentForm(payloadData);
      toast.success("Payment history updated successfully");

      // Reload so newly saved rows come back as read-only history rows
      await fetchERFData();
    } catch (err) {
      console.error("Update payment error:", err);
      toast.error("Failed to update payment history");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCollection = async () => {
    setLoading(true);
    try {
      const obj = {
        documentNumber: erfRecord.documentNumber,
        equipmentRentNr: erfRecord.equipmentRentNr,
        financeCollectionConfirmation: "Confirmed",
      };
      await ItemService.updateEquipmentRentForm(obj);
      setCollectionStatus("Confirmed");
      toast.success("Collection confirmed");
    } catch {
      toast.error("Failed to confirm collection");
    } finally {
      setLoading(false);
    }
  };

  const hasReceivedEntries =
    paymentHistory.some((r) => parseFloat(r.billingReceivedAmt) > 0) ||
    securityHistory.some((r) => parseFloat(r.securityReceivedAmt) > 0);

  const hasUnsavedRows =
    latestData.paymentHistory?.some((r) => r.local_id) ||
    latestData.securityHistory?.some((r) => r.local_id) ||
    latestData.creditNoteHistory?.some((r) => r.local_id);

  // ── RENDER ────────────────────────────────────────────────────────────────
  // if (loading) return <Loader isLoading={loading} className="text-center" />;

  // ── ASSIGNED SE PERMISSION ────────────────────────────────────────────────
  const assignedSEUserId = erfRecord?.serviceProviderCode || "";
  const currentUserId = userData?.data?.userId || "";
  const financeNotConfirmed = collectionStatus !== "Confirmed";

  const canSEAddPayment =
    isServiceExecutive &&
    !!assignedSEUserId &&
    assignedSEUserId === currentUserId;

  const effectiveAllowAddBilling =
    (isSalesAdmin || canSEAddPayment) && financeNotConfirmed && allowAddBilling;
  const effectiveAllowAddSecurity =
    (isSalesAdmin || canSEAddPayment) &&
    financeNotConfirmed &&
    allowAddSecurity;

  return (
    <div>
      <ERFPaymentAdjustmentDetails
        key={documentNumber}
        showSecurity={showSecurity}
        paymentHistory={paymentHistory}
        securityHistory={securityHistory}
        creditNoteHistory={creditNoteHistory}
        onDataChange={handleDataChange}
        serviceExecutiveList={serviceExecutiveList}
        allowAddBilling={effectiveAllowAddBilling}
        allowAddSecurity={effectiveAllowAddSecurity}
        allowAddCreditNote={allowAddCreditNote}
        isServiceExecutive={isServiceExecutive}
        isFinanceExecutive={financeExectiveUserRoleData}
        onConfirmRow={handleConfirmRow}
        defaultSalePoint={erfRecord?.salePoint ?? ""}
        defaultCostCenter={erfRecord?.costCenter ?? ""}
        isAdminOrPCD={isAdminOrPCD}
      />

      {/* <Row className="justify-content-end mt-2 mb-4 pe-3"> */}
      {hasReceivedEntries && (
        // <Row className="align-items-center mx-1 mb-3 p-2 border rounded bg-light">
        <Row className="align-items-center mx-1 mb-3 p-2 border rounded bg-light justify-content-between">
          <Col xs={6} md={3} className="pb-2 ps-2">
            <strong className="me-2">Finance Collection:</strong>
            {collectionStatus === "Confirmed" ? (
              <span className="badge bg-success">&#10003; Confirmed</span>
            ) : (
              <span className="badge bg-warning text-dark">Not Confirmed</span>
            )}
          </Col>
          {financeExectiveUserRoleData && collectionStatus !== "Confirmed" && (
            <Col xs={6} md={2} className="pb-2 ps-2">
              <button
                className="btnTable w-100"
                onClick={handleConfirmCollection}
                disabled={loading}
              >
                Confirm Collection
              </button>
            </Col>
          )}
        </Row>
      )}

      <Row className="justify-content-end mt-2 mb-4 pe-3">
        <Col xs={6} md={2} className="pb-2 ps-2">
          {allowUpdatePayentButton && (
            <button
              className="btnTable w-100"
              onClick={handleUpdatePayment}
              disabled={loading || !erfRecord || !hasUnsavedRows}
            >
              <i className="bi bi-save me-1" /> Save Payment
            </button>
          )}
        </Col>
      </Row>

      <FailureModal
        show={getShowFailureModal}
        onHide={() => setShowFailureModal(false)}
        failuremodaldata={failureModalData}
        requiredfields={getErrorFields}
      />
    </div>
  );
};

export default ERFPaymentSection;
